import React from "react";
import { Bot, ArrowUpRight, Check } from "lucide-react";

const TIERS = [
  {
    id: "basic",
    name: "Basic",
    price: "$125",
    delivery: "3 days",
    blurb: "For small businesses ready to capture leads 24/7.",
    features: [
      "1 chatbot trained on up to 20 FAQs",
      "Embed code for any website",
      "Basic branding (color + name)",
      "1 revision round",
    ],
    highlight: false,
  },
  {
    id: "standard",
    name: "Standard",
    price: "$275",
    delivery: "5 days",
    blurb: "The complete package most businesses need.",
    features: [
      "Trained on FAQs + business docs",
      "Lead capture + email notifications",
      "Full custom branding to match your site",
      "Welcome message + 3 quick-reply buttons",
      "2 revision rounds (within 14 days)",
    ],
    highlight: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: "$550",
    delivery: "7 days",
    blurb: "For businesses that want the full intelligent system.",
    features: [
      "Everything in Standard, plus:",
      "Calendar/booking integration (Calendly etc.)",
      "Admin dashboard to view conversations",
      "CRM lead-push (HubSpot, GoHighLevel)",
      "3 revisions + 30 days post-launch support",
    ],
    highlight: false,
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-cream-warm section-pad">
      <div className="container-edit">
        <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <Bot size={18} className="text-gold-deep" />
              <span className="eyebrow">Custom AI Chatbots</span>
            </div>
            <h2 className="h-section text-plum-deepest">
              One service. <span className="font-display-italic text-gold-deep">Done exceptionally well.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-muted-plum leading-relaxed text-lg font-body">
              Every Melavate chatbot is built by me, personally — trained on your real business,
              embedded on your site, and live within a week. Three tiers, transparent pricing,
              boutique-studio care at every level.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <article
              key={t.id}
              data-testid={`tier-card-${t.id}`}
              className={`${t.highlight ? "card-plum" : "card-soft"} flex flex-col relative`}
            >
              {t.highlight && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 inline-block text-[0.7rem] uppercase tracking-widest font-bold px-4 py-1.5 rounded-full bg-gold text-plum-deepest"
                  data-testid="popular-badge"
                >
                  Most Popular
                </span>
              )}
              <div className={`eyebrow mb-3 ${t.highlight ? "text-gold" : ""}`}>
                {t.name}
              </div>
              <div
                className={`font-display text-5xl mb-1 leading-none ${
                  t.highlight ? "text-cream" : "text-plum-deepest"
                }`}
              >
                {t.price}
              </div>
              <div
                className={`font-display-italic text-base mb-5 ${
                  t.highlight ? "text-gold" : "text-gold-deep"
                }`}
              >
                Delivered in {t.delivery}
              </div>
              <p
                className={`text-sm leading-relaxed mb-6 italic ${
                  t.highlight ? "text-cream/80" : "text-muted-plum"
                }`}
              >
                {t.blurb}
              </p>

              <ul className="space-y-3 mb-7 flex-1">
                {t.features.map((f, i) => (
                  <li
                    key={i}
                    className={`flex items-start gap-2.5 text-sm leading-snug ${
                      t.highlight ? "text-cream/90" : "text-plum-deep"
                    }`}
                  >
                    <Check
                      size={16}
                      className={`mt-0.5 flex-shrink-0 ${
                        t.highlight ? "text-gold" : "text-plum"
                      }`}
                      strokeWidth={2.5}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                data-testid={`tier-cta-${t.id}`}
                className={
                  t.highlight
                    ? "btn-gold w-full justify-center"
                    : "btn-secondary w-full justify-center"
                }
              >
                Start a project
                <ArrowUpRight size={16} />
              </a>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-plum font-display-italic">
            Not sure which tier? &nbsp;
            <a href="#contact" className="text-plum font-semibold underline-offset-4 hover:underline not-italic">
              Send me a note &mdash; I'll recommend the right one.
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}
